import React from 'react';
import { KBBlock } from '../../../types';

interface Props {
  block: KBBlock;
  isEditing: boolean;
  onChange: (updates: Partial<KBBlock>) => void;
  onFinish: () => void;
}

const defaultTable = { headers: ['Column 1', 'Column 2'], rows: [['', '']] };

const TableBlock: React.FC<Props> = ({ block, isEditing, onChange, onFinish }) => {
  const table = { ...defaultTable, ...(block.data ?? {}) };
  const headers: string[] = table.headers;
  const rows: string[][] = table.rows;

  const setTable = (updates: Partial<typeof defaultTable>) =>
    onChange({ data: { ...table, ...updates } });

  const updateHeader = (ci: number, val: string) => {
    const h = [...headers];
    h[ci] = val;
    setTable({ headers: h });
  };

  const updateCell = (ri: number, ci: number, val: string) => {
    const r = rows.map((row, i) => i === ri ? row.map((c, j) => j === ci ? val : c) : row);
    setTable({ rows: r });
  };

  const addRow = () => setTable({ rows: [...rows, headers.map(() => '')] });
  const addCol = () => {
    setTable({ headers: [...headers, `Column ${headers.length + 1}`], rows: rows.map((r) => [...r, '']) });
  };
  const removeRow = (ri: number) => setTable({ rows: rows.filter((_, i) => i !== ri) });
  const removeCol = (ci: number) => {
    setTable({ headers: headers.filter((_, i) => i !== ci), rows: rows.map((r) => r.filter((_, i) => i !== ci)) });
  };

  if (isEditing) {
    return (
      <div className="space-y-3">
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="w-full border-collapse">
            <thead className="bg-gray-50">
              <tr>
                {headers.map((h, ci) => (
                  <th key={ci} className="border border-gray-200 p-0 relative group">
                    <input
                      value={h}
                      onChange={(e) => updateHeader(ci, e.target.value)}
                      className="w-full px-3 py-2 font-semibold text-sm text-gray-700 bg-transparent border-none outline-none"
                    />
                    {headers.length > 1 && (
                      <button onClick={() => removeCol(ci)} className="absolute top-0.5 right-0.5 text-gray-400 hover:text-red-500 text-xs hidden group-hover:block">×</button>
                    )}
                  </th>
                ))}
                <th className="border border-gray-200 w-8">
                  <button onClick={addCol} className="w-full h-full text-gray-400 hover:text-blue-600 text-lg leading-none">+</button>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, ri) => (
                <tr key={ri} className="group">
                  {row.map((cell, ci) => (
                    <td key={ci} className="border border-gray-200 p-0">
                      <input
                        value={cell}
                        onChange={(e) => updateCell(ri, ci, e.target.value)}
                        className="w-full px-3 py-2 text-sm text-gray-700 bg-transparent border-none outline-none"
                      />
                    </td>
                  ))}
                  <td className="border border-gray-200">
                    <button onClick={() => removeRow(ri)} className="w-full text-gray-300 hover:text-red-500 text-xs hidden group-hover:block">×</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="flex gap-2">
          <button onClick={addRow} className="text-xs text-blue-600 hover:underline">+ Add row</button>
          <button onClick={onFinish} className="text-xs bg-blue-600 text-white px-3 py-1 rounded ml-auto">Done</button>
        </div>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="w-full border-collapse">
        <thead className="bg-gray-50">
          <tr>
            {headers.map((h, i) => (
              <th key={i} className="border border-gray-200 px-4 py-2 text-left text-sm font-semibold text-gray-700">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, ri) => (
            <tr key={ri} className="hover:bg-gray-50">
              {row.map((cell, ci) => (
                <td key={ci} className="border border-gray-200 px-4 py-2 text-sm text-gray-700">{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TableBlock;
